import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Boxes, Building2, Handshake, KeyRound, LayoutGrid, LineChart,
  Plug, ShieldCheck, UserPlus, Wrench,
} from 'lucide-react';
import StatsBand from './ui/StatsBand';
import HowItWorks from './ui/HowItWorks';
import Faq from './ui/Faq';
import CtaBand from './ui/CtaBand';
import Seo from './ui/Seo';

/**
 * /partners — how the platform reaches the market.
 *
 * CoPlaid is not sold direct. Implementation partners and
 * consultants resell it to their own clients, under their own
 * relationship. This page is written for that reader — a practice lead
 * deciding whether to add edge intelligence to what they already
 * deliver — not for the end operator.
 */

const STATS = [
  { num: 'Your brand', label: 'Customers never see CoPlaid' },
  { num: 'Your subdomain', label: 'Hosted on your tenant URL' },
  { num: 'One API', label: 'Provision customers programmatically' },
  { num: 'Your margin', label: 'A resale relationship, not a referral fee' },
];

const WHY = [
  {
    Icon: Boxes,
    title: 'An answer to what clients ask after go-live',
    body:
      'Clients ask what happens after go-live — how equipment gets watched, how people find out, what closes the gap. Almost no implementation practice can answer. Now you can, without building any of it.',
  },
  {
    Icon: Plug,
    title: 'Lands in the system they already use',
    body:
      'Where a native integration exists, your client’s team never leaves the system you implemented for them. Everything else connects over authenticated APIs. Either way it appears where the work already happens, which is why it gets adopted rather than abandoned.',
  },
  {
    Icon: KeyRound,
    title: 'You own the relationship',
    body:
      'You onboard the customer, you set the commercials, you provide the first line of support. CoPlaid is the platform underneath — not a vendor in the middle of your account.',
  },
  {
    Icon: LayoutGrid,
    title: 'Multi-tenant from the ground up',
    body:
      'Every customer is a separate account under yours, with isolated data and per-account feature control. Enable one capability for a client and a different one for the next.',
  },
  {
    Icon: ShieldCheck,
    title: 'Carrier compliance handled with you',
    body:
      'Reaching people at scale carries registration obligations. You collect the details in CoPlaid; we run the submission and carry the registration.',
  },
  {
    Icon: LineChart,
    title: 'Recurring revenue, not a referral fee',
    body:
      'This is a resale relationship. You price it into your engagement and keep the margin, on top of the implementation work you were already doing.',
  },
];

const STEPS = [
  {
    Icon: Handshake,
    title: 'Become a partner',
    text: 'A short technical and commercial onboarding. You get a partner account, API credentials, and your own tenant subdomain.',
  },
  {
    Icon: UserPlus,
    title: 'Add your first client',
    text: 'Create the customer from the partner console, or provision them programmatically through the partner API. Choose which products they get.',
  },
  {
    Icon: Building2,
    title: 'Deploy into their environment',
    text: 'Install into the system they already run on where a native integration exists, or connect over the API. Either way it appears where their team already works.',
  },
  {
    Icon: ShieldCheck,
    title: 'Register for messaging',
    text: 'Collect the client’s business details in CoPlaid. We handle the registry submission and the carrier relationship.',
  },
  {
    Icon: Wrench,
    title: 'Run and support',
    text: 'You front the relationship. We keep the platform running, and back you up on anything that reaches the infrastructure.',
  },
];

const FAQS = [
  {
    q: 'Can we white-label it?',
    a:
      'Yes, in the sense that matters commercially: your customers are provisioned through your partner API and never create a CoPlaid account or see a CoPlaid invoice, and your tenant runs on its own subdomain. Talk to us about how far you want to take the branding — we’d rather scope that honestly than overpromise on a landing page.',
  },
  {
    q: 'Do you sell direct to our clients?',
    a:
      'No. The platform goes to market through partners. If an organisation approaches us directly and you are already working with them, the engagement is yours.',
  },
  {
    q: 'What do we have to build?',
    a:
      'Nothing to use it. Where a native integration already exists, standing it up in a client account is configuration rather than development. If you want to go deeper, everything is available over authenticated HTTP APIs.',
  },
  {
    q: 'Which systems does it connect to?',
    a:
      'Some are already integrated and install directly into the client’s environment; anything else is reachable over authenticated APIs. Tell us what your clients run and we will tell you which of those two it is, rather than leave you to find out during an engagement.',
  },
  {
    q: 'Who handles messaging compliance?',
    a:
      'Reaching people at scale in the US requires each business to be registered with the carrier registry. You gather the client’s legal details — legal name, tax ID, authorised representative — in CoPlaid; we review them and run the submission. Registrations sit under our platform, so you are not establishing a carrier relationship for every client.',
  },
  {
    q: 'How is it priced?',
    a:
      'Partner pricing depends on the products, the number of client accounts, and messaging volume. It is a conversation rather than a page — get in touch and we will put a number against your actual pipeline.',
  },
];

const Partners = () => {
  const navigate = useNavigate();

  return (
    <div>
      <Seo
        path="/partners"
        title="CoPlaid for Partners"
        description="CoPlaid goes to market through implementation partners and consulting practices. Add edge intelligence to what you already deliver, under your own brand and your own commercial relationship."
      />

      <section className="cp-hero">
        <div className="cp-container cp-hero-grid">
          <div>
            <p className="cp-eyebrow">For partners &amp; implementation consultants</p>
            <h1 className="cp-h1">Add intelligent operations to what you already deliver.</h1>
            <p className="cp-lede">
              CoPlaid is not sold direct. It reaches the market through the consultants and
              implementation teams who already run the systems their clients depend on &mdash;
              under your brand, your relationship, and your commercials.
            </p>
            <div className="cp-hero-actions">
              <button className="cp-btn cp-btn--primary cp-btn--lg" onClick={() => navigate('/contact')}>
                Become a partner <ArrowRight size={18} />
              </button>
              <button className="cp-btn cp-btn--ghost cp-btn--lg" onClick={() => navigate('/product')}>
                See the platform
              </button>
            </div>
            <p className="cp-hero-meta">
              <strong>Your brand.</strong> Your relationship. Your commercials.
            </p>
          </div>

          {/* The account tree is the pitch: your practice on top, your clients
              underneath, CoPlaid invisible. Drawn rather than screenshotted so
              it stays honest as the console changes. */}
          <div className="cp-hero-visual">
            <div className="cp-screenshot cp-screenshot--ink" style={{ padding: 28 }}>
              <div style={{ display: 'grid', gap: 12, fontFamily: 'var(--font-sans)', color: '#fff' }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>
                  Your partner account
                </div>
                <div style={{ padding: '14px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>yourfirm.coplaid.ai</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
                    Your brand &middot; your commercials
                  </div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginTop: 6 }}>
                  Your clients
                </div>
                {[
                  { name: 'Northwind Services', products: 'Full platform' },
                  { name: 'Harbor Mechanical', products: 'Agents' },
                  { name: 'Fielding Medical', products: 'Messaging' },
                ].map((c) => (
                  <div
                    key={c.name}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '11px 14px', marginLeft: 18,
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)', borderRadius: 9,
                    }}
                  >
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)' }}>{c.name}</span>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{c.products}</span>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
                  Isolated data &middot; per-account capabilities &middot; each in their own environment
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <StatsBand items={STATS} />

      <section className="cp-section">
        <div className="cp-container">
          <h2 className="cp-h2">Why partners take this on.</h2>
          <p className="cp-lede">
            You already own the client relationship and you already know their operation.
            What you have not had is anything to sell them after go-live.
          </p>
          <div className="cp-grid-3">
            {WHY.map(({ Icon, title, body }) => (
              <div className="cp-card" key={title}>
                <span className="cp-card-icon"><Icon size={20} /></span>
                <div className="cp-card-text">
                  <h3 className="cp-card-title">{title}</h3>
                  <p>{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cp-section cp-section--surface">
        <div className="cp-container">
          <h2 className="cp-h2">From partner agreement to a client up and running.</h2>
          <HowItWorks steps={STEPS} />
        </div>
      </section>

      <section className="cp-section">
        <div className="cp-container">
          <h2 className="cp-h2">What sits underneath.</h2>
          <p className="cp-lede">
            The platform was built multi-tenant from the start, because this is how it was
            always going to be sold.
          </p>
          <div className="cp-grid-2">
            <div className="cp-card">
              <div className="cp-card-text">
                <h3 className="cp-card-title">Accounts nest the way your business does</h3>
                <p>
                  Your partner account sits above your client accounts. You see and manage all of
                  them; they see only their own. Data is isolated per customer, and products are
                  enabled per account &mdash; one capability for one client, another for the next, both
                  for the ones that need it.
                </p>
              </div>
            </div>
            <div className="cp-card">
              <div className="cp-card-text">
                <h3 className="cp-card-title">Provision clients from your own systems</h3>
                <p>
                  An authenticated partner API creates a customer account, configures its products,
                  and returns credentials &mdash; so onboarding can live inside your own workflow
                  instead of a console someone has to remember to use.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cp-section cp-section--surface">
        <div className="cp-container">
          <h2 className="cp-h2">Questions partners ask first.</h2>
          <Faq items={FAQS} />
        </div>
      </section>

      <CtaBand
        title="Let’s talk about your client base."
        sub="Tell us who you implement for and what they keep asking for after go-live. We’ll tell you honestly whether CoPlaid fits."
        primary={{ label: 'Become a partner', to: '/contact' }}
        secondary={{ label: 'See the product', to: '/product' }}
      />
    </div>
  );
};

export default Partners;
